import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const { addToCart: addToCartContext } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState('');
  const [setupType, setSetupType] = useState('Indoor');
  const [wireLength, setWireLength] = useState('1m');
  const [addedToCart, setAddedToCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        
        // Try to fetch from all product collections
        const responses = await Promise.all([
          fetch(`${backendUrl}/api/products/statues`),
          fetch(`${backendUrl}/api/products/lightings`),
          fetch(`${backendUrl}/api/products/coffee-tables`)
        ]);
        
        const [statues, lightings, coffeeTables] = await Promise.all(
          responses.map(r => r.json())
        );
        
        // Combine all products and find the one with matching id
        const allProducts = [...statues, ...lightings, ...coffeeTables];
        const foundProduct = allProducts.find(p => p.id === id);
        
        if (foundProduct) {
          setProduct(foundProduct);
          // Set default size if product has sizes (skip sold sizes)
          if (foundProduct.sizes) {
            // Try to find first non-sold size, prioritizing M
            const availableSizes = Object.keys(foundProduct.sizes).filter(
              size => !foundProduct.sizes[size].sold
            );
            const defaultSize = availableSizes.includes('M') 
              ? 'M' 
              : availableSizes[0] || Object.keys(foundProduct.sizes)[0];
            setSelectedSize(defaultSize);
          }
          // Set default color if product has color variants (skip sold colors)
          if (foundProduct.colorVariants) {
            const availableColors = Object.keys(foundProduct.colorVariants).filter(
              color => !foundProduct.colorVariants[color].sold
            );
            const defaultColor = availableColors[0] || Object.keys(foundProduct.colorVariants)[0];
            setSelectedColor(defaultColor);
            // Set image based on default color variant
            if (foundProduct.colorVariants[defaultColor]?.imageIndex !== undefined) {
              setSelectedImage(foundProduct.colorVariants[defaultColor].imageIndex);
            }
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  // Check if product is a sculpture (unique pieces - no quantity selector needed)
  const isSculpture = () => {
    if (!product) return false;
    const category = product.category?.toLowerCase() || '';
    return category.includes('sculpture') || category.includes('statue');
  };

  // Get back link based on product category
  const getBackLink = () => {
    if (!product) return '/';
    
    const category = product.category?.toLowerCase() || '';
    
    if (category.includes('lighting') || category.includes('chicken cage')) {
      return '/bird-cage-lightings';
    } else if (category.includes('sculpture') || category.includes('statue')) {
      return '/sculpture';
    } else if (category.includes('coffee') || category.includes('table')) {
      return '/coffee-tables';
    }
    
    return '/';
  };

  const handleAddToCart = () => {
    try {
      // Create cart item with proper format for CartContext
      const cartItem = {
        id: `${product.id}-${product.sizes ? selectedSize : 'default'}-${product.category === 'Chicken Cage Lightings' ? `${setupType}-${wireLength}` : ''}`, // Unique ID per configuration
        productId: product.id, // Original product ID
        name: product.name,
        category: product.category,
        image: getCurrentImage(),
        // For products with sizes
        ...(product.sizes && {
          selectedSize: selectedSize,
          price: product.category === 'Chicken Cage Lightings' 
            ? `CHF ${getTotalPrice()}`
            : (product.sizes[selectedSize]?.price || `CHF ${getCurrentPrice()}`)
        }),
        // For products without sizes
        ...(!product.sizes && {
          price: `CHF ${getCurrentPrice()}`
        }),
        // For lighting products, include setup and wire length
        ...(product.category === 'Chicken Cage Lightings' && {
          setupType: setupType,
          wireLength: wireLength,
          basePrice: getCurrentPrice(),
          setupCost: getSetupCost(),
          wireCost: getWireLengthCost()
        })
      };
      
      console.log('Adding to cart:', cartItem);
      
      // Add to cart using context (it will handle quantity)
      for (let i = 0; i < quantity; i++) {
        addToCartContext(cartItem);
      }
      
      setAddedToCart(true);
      
      // Try to show toast
      try {
        toast({
          title: "Added to cart",
          description: `${product.name}${product.sizes ? ` (Size ${selectedSize})` : ''} has been added to your cart.`,
        });
      } catch (toastError) {
        console.error('Toast error:', toastError);
      }
      
      setTimeout(() => setAddedToCart(false), 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-xl mb-4">{error || 'Product not found'}</div>
          <Link
            to="/bird-cage-lightings"
            className="text-[hsl(45,100%,70%)] hover:underline"
          >
            Back to lightings
          </Link>
        </div>
      </div>
    );
  }

  // Get current image - uses selectedImage from state which can be set by thumbnails or size changes
  const getCurrentImage = () => {
    if (!product.images || product.images.length === 0) return '/placeholder.png';
    
    // Use selectedImage state (set by thumbnail clicks or size changes)
    const img = product.images[selectedImage] || product.images[0];
    
    if (!img) return '/placeholder.png';
    
    let imageUrl;
    
    // If it's already a full URL (starts with http), return as-is
    if (img.startsWith('http')) {
      imageUrl = img;
    } else if (img.startsWith('/')) {
      // If it starts with /, encode the path properly
      const parts = img.split('/');
      imageUrl = parts.map(part => encodeURIComponent(part)).join('/').replace(/%2F/g, '/');
    } else {
      // Otherwise, it's a relative path - add / and encode
      imageUrl = '/' + encodeURIComponent(img);
    }
    
    // Add cache-busting parameter if product has updated_at timestamp
    if (product.updated_at) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      imageUrl += `${separator}v=${encodeURIComponent(product.updated_at)}`;
    }
    
    return imageUrl;
  };

  // Get current price based on size selection or color variant
  const getCurrentPrice = () => {
    // Check for color variants price first
    if (product.colorVariants && selectedColor && product.colorVariants[selectedColor]) {
      const price = product.colorVariants[selectedColor].price;
      // Extract number from price string
      const match = price?.match(/[\d,.]+/);
      return match ? parseFloat(match[0].replace(',', '')) : 0;
    }
    
    // Check for size-based pricing
    if (product.sizes && product.sizes[selectedSize]) {
      const price = product.sizes[selectedSize].price;
      // Extract number from price string like "CHF 440.00"
      const match = price?.match(/[\d,.]+/);
      return match ? parseFloat(match[0].replace(',', '')) : 0;
    }
    
    // Parse base price from various formats
    if (typeof product.price === 'number') return product.price;
    if (typeof product.price === 'string') {
      const match = product.price.match(/[\d,.]+/);
      return match ? parseFloat(match[0].replace(',', '')) : 0;
    }
    return 0;
  };

  // Calculate additional costs for lighting products
  const getSetupCost = () => {
    if (!product || product.category !== 'Chicken Cage Lightings') return 0;
    return setupType === 'Outdoor' ? 20 : 0;
  };

  const getWireLengthCost = () => {
    if (!product || product.category !== 'Chicken Cage Lightings') return 0;
    if (wireLength === '2m') return 20;
    if (wireLength === '3m') return 30;
    return 0;
  };

  const getTotalPrice = () => {
    const basePrice = getCurrentPrice();
    const setupCost = getSetupCost();
    const wireCost = getWireLengthCost();
    return basePrice + setupCost + wireCost;
  };

  return (
    <div className="min-h-screen bg-black pt-20 md:pt-24">
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-12 max-w-7xl">
        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-square overflow-hidden rounded-2xl bg-gray-900 relative">
              <img
                src={getCurrentImage()}
                alt={product.name}
                className={`w-full h-full object-cover hover:scale-105 transition-transform duration-700 ${
                  (product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold))
                    ? 'opacity-80'
                    : ''
                }`}
              />
              
              {/* SOLD Tag Overlay - Show if entire product is sold OR if selected color is sold */}
              {(product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold)) && (
                <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-black bg-opacity-30">
                  <div className="bg-black bg-opacity-70 px-8 py-4 rounded-lg">
                    <span className="text-white text-3xl font-bold tracking-wider">SOLD OUT</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Image thumbnails if multiple images */}
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((img, idx) => {
                  const imgSrc = img?.startsWith('http') || img?.startsWith('/') ? img : `/${encodeURIComponent(img)}`;
                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square overflow-hidden rounded-lg border-2 transition-colors ${
                        selectedImage === idx
                          ? 'border-[hsl(45,100%,70%)]'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <img
                        src={imgSrc}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/placeholder.png';
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-4 md:space-y-6">
            <div>
              <p className="text-[hsl(45,100%,70%)] text-sm font-medium mb-2 tracking-wider uppercase">
                {product.category}
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
                {product.name}
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* RGB Smart LED Information (for Chicken Cage Lightings only) */}
            {product.category === 'Chicken Cage Lightings' && (
              <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-2">Smart RGB LED Included</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Every cage includes a full <span className="text-purple-400 font-semibold">RGB smart LED bulb</span> that can be controlled via a custom <span className="text-blue-400 font-semibold">iOS/Android compatible app</span>. Create the perfect ambiance with unlimited color combinations and brightness control.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Color Selector (if product has color variants) */}
            {product.colorVariants && (
              <div className="py-6 border-t border-gray-800">
                <label className="text-white font-medium mb-3 block">Select Color:</label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(product.colorVariants).map((color) => {
                    const colorData = product.colorVariants[color];
                    const isSold = colorData.sold || false;
                    
                    return (
                      <button
                        key={color}
                        onClick={() => {
                          if (isSold) return; // Disable click for sold colors
                          setSelectedColor(color);
                          // Update selected image based on color variant
                          if (colorData?.imageIndex !== undefined) {
                            setSelectedImage(colorData.imageIndex);
                          }
                        }}
                        disabled={isSold}
                        className={`relative px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                          isSold
                            ? 'cursor-not-allowed border-gray-400 bg-gray-300 text-gray-500'
                            : selectedColor === color
                            ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                            : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                        }`}
                      >
                        {isSold ? (
                          <span className="text-gray-600 font-bold">{color} - SOLD</span>
                        ) : (
                          color
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size Selector (if product has sizes) */}
            {product.sizes && (
              <div className={`py-6 ${product.colorVariants ? '' : 'border-t'} border-gray-800`}>
                <label className="text-white font-medium mb-3 block">Select Size:</label>
                <div className="grid grid-cols-4 gap-3">
                  {Object.keys(product.sizes).map((size) => {
                    const sizeData = product.sizes[size];
                    const isSold = sizeData.sold || false;
                    
                    return (
                      <button
                        key={size}
                        onClick={() => {
                          if (isSold) return; // Disable click for sold sizes
                          setSelectedSize(size);
                          // Update selected image based on size
                          if (sizeData?.imageIndex !== undefined) {
                            setSelectedImage(sizeData.imageIndex);
                          }
                        }}
                        disabled={isSold}
                        className={`relative px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                          isSold
                            ? 'cursor-not-allowed border-gray-400 bg-gray-300 text-gray-400'
                            : selectedSize === size
                            ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                            : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                        }`}
                      >
                        {isSold ? (
                          <span className="text-gray-600 font-bold">SOLD</span>
                        ) : (
                          size
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Setup Type (for Lighting products only) */}
            {product.category === 'Chicken Cage Lightings' && (
              <div className="py-6 border-t border-gray-800">
                <label className="text-white font-medium mb-3 block">Setup Type:</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSetupType('Indoor')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                      setupType === 'Indoor'
                        ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                        : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                    }`}
                  >
                    Indoor
                    <span className="block text-xs mt-1 opacity-80">(No extra charge)</span>
                  </button>
                  <button
                    onClick={() => setSetupType('Outdoor')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                      setupType === 'Outdoor'
                        ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                        : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                    }`}
                  >
                    Outdoor
                    <span className="block text-xs mt-1 opacity-80">(+CHF 20)</span>
                  </button>
                </div>
              </div>
            )}

            {/* Wire Length (for Lighting products only) */}
            {product.category === 'Chicken Cage Lightings' && (
              <div className="py-6 border-t border-gray-800">
                <label className="text-white font-medium mb-3 block">Wire Length:</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setWireLength('1m')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                      wireLength === '1m'
                        ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                        : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                    }`}
                  >
                    1 meter
                    <span className="block text-xs mt-1 opacity-80">(Standard)</span>
                  </button>
                  <button
                    onClick={() => setWireLength('2m')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                      wireLength === '2m'
                        ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                        : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                    }`}
                  >
                    2 meters
                    <span className="block text-xs mt-1 opacity-80">(+CHF 20)</span>
                  </button>
                  <button
                    onClick={() => setWireLength('3m')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border-2 transition-all duration-200 ${
                      wireLength === '3m'
                        ? 'border-[hsl(45,100%,70%)] bg-[hsl(45,100%,70%)] text-black'
                        : 'border-gray-700 bg-gray-900 text-white hover:border-[hsl(45,100%,70%)]'
                    }`}
                  >
                    3 meters
                    <span className="block text-xs mt-1 opacity-80">(+CHF 30)</span>
                  </button>
                </div>
              </div>
            )}

            {/* Price */}
            <div className={`py-6 border-b border-gray-800 ${!product.sizes && product.category !== 'Chicken Cage Lightings' ? 'border-t' : ''}`}>
              {product.category === 'Chicken Cage Lightings' ? (
                <div className="space-y-3">
                  <div className="text-white space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Base Price ({selectedSize}):</span>
                      <span>CHF {getCurrentPrice()}</span>
                    </div>
                    {setupType === 'Outdoor' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Outdoor Setup:</span>
                        <span>CHF 20</span>
                      </div>
                    )}
                    {wireLength !== '1m' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Wire Length ({wireLength}):</span>
                        <span>CHF {getWireLengthCost()}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-gray-700 flex justify-between items-baseline">
                      <span className="text-lg font-medium">Total:</span>
                      <span className="text-3xl font-bold text-[hsl(45,100%,70%)]">
                        CHF {getTotalPrice()}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white">
                    {product.sizes ? (
                      `CHF ${getCurrentPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    ) : product.colorVariants && selectedColor ? (
                      `CHF ${getCurrentPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    ) : (
                      `CHF ${getCurrentPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Specifications</h3>
              <div className="space-y-3">
                {product.material && (
                  <div className="flex justify-between py-3 border-b border-gray-800">
                    <span className="text-gray-400">Material</span>
                    <span className="text-white font-medium">{product.material}</span>
                  </div>
                )}
                {(product.dimensions || (product.sizes && product.sizes[selectedSize]?.dimensions) || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.dimensions)) && (
                  <div className="flex justify-between py-3 border-b border-gray-800">
                    <span className="text-gray-400">Dimensions</span>
                    <span className="text-white font-medium">
                      {product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.dimensions
                        ? `${product.colorVariants[selectedColor].dimensions.height} × ${product.colorVariants[selectedColor].dimensions.width} × ${product.colorVariants[selectedColor].dimensions.depth}`
                        : product.sizes && product.sizes[selectedSize]?.dimensions
                        ? `${product.sizes[selectedSize].dimensions.diameter}, Height ${product.sizes[selectedSize].dimensions.height}`
                        : typeof product.dimensions === 'object' 
                        ? `${product.dimensions.height} × ${product.dimensions.width} × ${product.dimensions.depth}`
                        : product.dimensions
                      }
                    </span>
                  </div>
                )}
                {product.category && (
                  <div className="flex justify-between py-3 border-b border-gray-800">
                    <span className="text-gray-400">Category</span>
                    <span className="text-white font-medium capitalize">{product.category}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-b border-gray-800">
                  <span className="text-gray-400">Availability</span>
                  <span className={`font-medium ${product.available ? 'text-green-400' : 'text-red-400'}`}>
                    {product.available ? 'In Stock' : 'Sold Out'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="space-y-4 pt-4 md:pt-6 pb-6 md:pb-0">
              {/* Quantity selector - only show for non-sculpture products (lightings, etc.) */}
              {!isSculpture() && (
                <div className="flex items-center gap-4">
                  <label className="text-white font-medium text-sm md:text-base">Quantity:</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 rounded-full border-2 border-gray-700 text-white hover:border-[hsl(45,100%,70%)] hover:text-[hsl(45,100%,70%)] transition-colors"
                    >
                      -
                    </button>
                    <span className="text-white font-bold w-12 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 rounded-full border-2 border-gray-700 text-white hover:border-[hsl(45,100%,70%)] hover:text-[hsl(45,100%,70%)] transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={product.sold || addedToCart || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold) || (product.sizes && product.sizes[selectedSize]?.sold)}
                className={`w-full py-3 md:py-4 rounded-full font-bold text-base md:text-lg flex items-center justify-center gap-3 transition-all duration-300 ${
                  (product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold) || (product.sizes && product.sizes[selectedSize]?.sold))
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-60'
                    : addedToCart
                    ? 'bg-green-600 text-white'
                    : 'bg-[hsl(45,100%,70%)] text-black hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] md:hover:scale-105'
                }`}
              >
                {(product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold) || (product.sizes && product.sizes[selectedSize]?.sold)) ? (
                  <>
                    SOLD OUT
                  </>
                ) : addedToCart ? (
                  <>
                    <Check className="w-6 h-6" />
                    Added to cart
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-6 h-6" />
                    Add to cart
                  </>
                )}
              </button>

              <Link
                to="/cart"
                className="block w-full py-4 rounded-full border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] font-bold text-lg text-center hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300"
              >
                View cart
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation back to gallery */}
        <div className="mt-12 text-center pb-24 md:pb-0">
          <Link
            to={getBackLink()}
            className="inline-block px-8 py-3 border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] font-bold rounded-full hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300"
          >
            Back to Gallery
          </Link>
        </div>
      </div>

      {/* Mobile Sticky Buy Button - Only visible on small screens */}
      {product && (
        <div className="block lg:hidden fixed bottom-0 left-0 right-0 bg-black border-t-2 border-gray-800 p-4 z-[9999] shadow-2xl">
          <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
            <div className="flex flex-col min-w-0">
              <span className="text-gray-400 text-xs">Total Price</span>
              <span className="text-white font-bold text-lg md:text-xl whitespace-nowrap">
                CHF {getTotalPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={product?.sold || addedToCart || (product?.colorVariants && selectedColor && product?.colorVariants[selectedColor]?.sold) || (product?.sizes && product?.sizes[selectedSize]?.sold)}
              className={`flex-1 py-3 px-4 rounded-full font-bold text-sm md:text-base flex items-center justify-center gap-2 transition-all duration-300 whitespace-nowrap ${
                (product?.sold || (product?.colorVariants && selectedColor && product?.colorVariants[selectedColor]?.sold) || (product?.sizes && product?.sizes[selectedSize]?.sold))
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-60'
                  : addedToCart
                  ? 'bg-green-600 text-white'
                  : 'bg-[hsl(45,100%,70%)] text-black active:scale-95'
              }`}
            >
              {(product?.sold || (product?.colorVariants && selectedColor && product?.colorVariants[selectedColor]?.sold) || (product?.sizes && product?.sizes[selectedSize]?.sold)) ? (
                'SOLD OUT'
              ) : addedToCart ? (
                <>
                  <Check className="w-4 h-4 md:w-5 md:h-5" />
                  Added
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
                  Add to cart
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;